import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Compass, Sparkles, Gift, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export const REFERRAL_LANDING_TESTIDS = {
  page: "referral-landing-page",
  referrerName: "referral-referrer-name",
  cta: "referral-cta-btn",
  invalid: "referral-invalid",
};

export default function ReferralLandingPage() {
  const { code } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // store the code so AuthPage can attach it during signup
    if (code) localStorage.setItem("tb_ref_code", code.toLowerCase());
    (async () => {
      try {
        const { data } = await api.get(`/referral/${code}`);
        setInfo(data);
      } catch {
        setInfo({ valid: false });
      } finally { setLoading(false); }
    })();
  }, [code]);

  const startJourney = () => nav(`/auth?ref=${code}&mode=register`);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-tb-navy-deep">
        <span className="font-hand text-tb-gold-bright text-2xl">~ checking your invite ~</span>
      </div>
    );
  }

  return (
    <div data-testid={REFERRAL_LANDING_TESTIDS.page}
      className="min-h-screen w-full flex items-center justify-center bg-tb-navy-deep px-4 py-10">
      <div className="tb-frame w-full max-w-md md:rounded-[2rem] overflow-hidden">
        {/* Header */}
        <div className="bg-tb-navy text-tb-gold border-b-2 border-tb-gold/70 px-6 pt-7 pb-6 relative">
          <div className="absolute right-4 top-4 tb-spin-slow rounded-full border border-tb-gold/60 p-2 opacity-80">
            <Compass className="w-5 h-5 text-tb-gold" strokeWidth={1.5} />
          </div>
          <BrandLogo size="md" theme="navy" />
          <p className="font-hand text-tb-gold/80 mt-2 text-base italic">
            your lifelong travel journal
          </p>
        </div>

        {/* Body */}
        <div className="tb-paper-soft px-6 py-8">
          {info?.valid ? (
            <>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-tb-blue mb-1">
                  <Sparkles className="w-4 h-4" />
                  <p className="font-hand text-tb-blue text-lg italic">you've been invited</p>
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="mt-2 flex items-center justify-center gap-3">
                  {info.picture ? (
                    <img src={info.picture} alt={info.name}
                      className="w-14 h-14 rounded-full border-2 border-tb-gold object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-full border-2 border-tb-gold bg-tb-gold/20 flex items-center justify-center font-serif-it text-2xl text-tb-navy">
                      {info.name?.[0]?.toUpperCase() || "✦"}
                    </div>
                  )}
                  <div className="text-left">
                    <div className="font-serif-it text-tb-navy text-2xl leading-tight"
                      data-testid={REFERRAL_LANDING_TESTIDS.referrerName}>
                      {info.name}
                    </div>
                    <div className="font-hand text-tb-navy/60 text-sm">invited you</div>
                  </div>
                </div>

                <p className="mt-5 font-serif-it text-tb-navy text-xl leading-relaxed">
                  "Every journey is better with a partner in dreaming."
                </p>

                <div className="mt-5 rounded-2xl bg-tb-coral-soft border-2 border-tb-coral/40 px-4 py-3 text-left flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-full bg-tb-coral text-white flex items-center justify-center">
                    <Gift className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif-it text-tb-navy text-lg leading-tight">
                      The deal
                    </div>
                    <div className="font-hand text-tb-navy/80 text-sm">
                      You start your own journal · {info.name} gets <b className="text-tb-coral">+250 pts</b> toward their next adventure.
                    </div>
                  </div>
                </div>

                <button
                  data-testid={REFERRAL_LANDING_TESTIDS.cta}
                  onClick={startJourney}
                  className="mt-6 w-full py-3 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-xl border-2 border-tb-gold tb-tap hover:bg-tb-navy-soft flex items-center justify-center gap-2"
                >
                  Start my journal <ArrowRight className="w-5 h-5" />
                </button>
                <Link to="/auth" className="block mt-3 font-hand text-tb-navy/60 hover:text-tb-coral text-sm">
                  already have a journal? log in →
                </Link>
              </div>
            </>
          ) : (
            <div data-testid={REFERRAL_LANDING_TESTIDS.invalid} className="text-center py-4">
              <p className="font-serif-it text-tb-navy text-2xl">Hmm — that invite isn't valid.</p>
              <p className="font-hand text-tb-navy/70 mt-2 text-base">
                The link may have expired, but the road is still open.
              </p>
              <button onClick={() => nav("/auth")}
                className="mt-5 px-5 py-3 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-lg border-2 border-tb-gold tb-tap">
                Open TravelBio →
              </button>
            </div>
          )}

          <p className="text-center font-hand text-xs italic text-tb-navy/50 mt-6">
            Dream it. Plan it. Book it. Cherish it.
          </p>
        </div>
      </div>
    </div>
  );
}
